import { Component, computed, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { Subscription, switchMap } from 'rxjs';
import { ImageCacheService } from 'src/app/services/image-cache.service';
import { IUserNotificationPreference, UsersService } from 'src/app/services/users.service';
import { RightSidebarContainerComponent } from '../right-sidebar-container/right-sidebar-container.component';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatRadioModule } from '@angular/material/radio';
import { ProfileImageUploadComponent } from '../profile-upload-image/profile-image-upload.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MainNavigationComponent } from '../main-navigation/main-navigation.component';
import { RouterModule } from '@angular/router';
import { LeftSidebarComponent } from '../left-sidebar/left-sidebar.component';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { Role } from 'src/app/models/user/role.model';
import { toSignal } from '@angular/core/rxjs-interop';
import { PhoneInputDirective } from 'src/app/modules/shared/directives/phone-input.directive';
import { tenDigitPhoneValidator } from 'src/app/modules/shared/validators/phone.validator';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    standalone: true,
    imports: [
      MatSidenavModule,
      RightSidebarContainerComponent,
      ReactiveFormsModule,
      MatSlideToggleModule,
      MatRadioModule,
      ProfileImageUploadComponent,
      MatFormFieldModule,
      MainNavigationComponent,
      RouterModule,
      LeftSidebarComponent,
      MatInputModule,
      MatButtonModule,
      PhoneInputDirective
    ]
})
export class HomeComponent implements OnInit{
  @ViewChild('drawer') drawer: MatDrawer;
  selectedAction = '';
  selectedFile: File;
  userId: string;
  userProfileUrl: any;
  organizationName = '';

  preferencesform: FormGroup = new FormGroup({
    modified: new FormControl(true),
    reachFinalState: new FormControl(true),
    expiring: new FormControl(true),
    frequency: new FormControl(0)
  });
  profileForm: FormGroup = new FormGroup({
    firstName: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required]),
    phoneNumber: new FormControl('', {
      validators: [tenDigitPhoneValidator()],
      updateOn: 'blur'
    }),
  });

  private subscriptions = new Subscription();

  readonly userRole = toSignal(this.usersService.userRole$, { initialValue: undefined });
  readonly isLender = computed(() => this.userRole() === Role.Lender);
  readonly isProvider = computed(() => this.userRole() === Role.Provider);
  readonly isAdmin = computed(() => this.userRole() === Role.Admin);
  readonly isSystemUser = computed(() => this.userRole() === Role.SystemUser);
  readonly isConsultant = computed(() => this.userRole() === Role.Consultant);
  readonly isProviderReadOnly = computed(() => this.userRole() === Role.ProviderReadOnly);
  readonly isLenderReadOnly = computed(() => this.userRole() === Role.LenderReadOnly);

  constructor(
    private usersService: UsersService,
    private imageCacheService: ImageCacheService
  ) {}

  ngOnInit() {
    const userSub = this.usersService.getUserProfile()
      .pipe(
        switchMap((profile: any) => {
          this.userId = profile.data.id.toString();
          this.organizationName = this.isLender() || this.isLenderReadOnly() || this.isProvider() || this.isConsultant() || this.isProviderReadOnly()
            ? profile.data.organizationName
              : 'Proteris Compliance Solutions';
          this.profileForm.setValue({
            firstName: profile.data.firstName,
            lastName: profile.data.lastName,
            phoneNumber: profile.data.phoneNumber ?? '',
          });

          const cachedImage = this.imageCacheService.getImageFile(this.userId);
          if (cachedImage) {
            return this.imageCacheService.convertFileToDataUrl(cachedImage);
          } else {
            return this.usersService.getUserImageProfile(this.userId);
          }
        })
      )
      .subscribe((res: any) => {
        if (typeof res === 'string') { // from Cache
          this.userProfileUrl = res;
        } else if (res.data.profileImage) {
          const imageFile = this.imageCacheService.base64ToFile(res.data.profileImage);
          this.imageCacheService.cacheImage(this.userId, imageFile);
          this.imageCacheService.convertFileToDataUrl(imageFile).subscribe(url => this.userProfileUrl = url);
        }
      });

    this.subscriptions.add(userSub);

    this.loadPreferences();
  }

  onOpenPreferences() {
    this.selectedAction = 'preferences';
    this.drawer.toggle();
  }
  onOpenProfile() {
    this.selectedAction = 'profile';
    this.drawer.toggle();
  }

  updatePreferences() {
    let data = this.preferencesform.value;
    const preferences: IUserNotificationPreference = {
      events: [
        {
          event: 'Modified',
          isActive: data.modified
        },
        {
          event: 'ReachedFinalState',
          isActive: data.reachFinalState
        },
        {
          event: 'Expiring',
          isActive: data.expiring
        }
      ],
      frequency: data.frequency
    }
    this.usersService.changeUserPreferences(preferences).subscribe(_ => {
      this.drawer.close();
      this.loadPreferences();
    })
  }

  onImageSelected(file: File) {
    this.selectedFile = file;
  }

  updateProfile() {
    let data = this.profileForm.value;
    data.profileImage = this.selectedFile;

    this.usersService.updateUserProfile(data).subscribe(_=> {

      if(data.profileImage) {
        this.imageCacheService.cacheImage(this.userId, this.selectedFile);
        this.imageCacheService.invalidateProfileUrl(this.userId);
        const reader = new FileReader();
        reader.onload = (e) => this.userProfileUrl = reader.result;
        reader.readAsDataURL(this.selectedFile);
      }

    })

    this.drawer.close();
  }

  close() {
    if (this.selectedAction === 'preferences') {
      this.loadPreferences();
    }
    this.drawer.close();
  }

  private loadPreferences() {
    this.usersService.getPreferences().subscribe(response => {
      if (!response.isError) {
        const modified = response.data.events?.find(ev => ev.event == 'Modified')?.isActive;
        const finalState = response.data.events?.find(ev => ev.event == 'ReachedFinalState')?.isActive;
        const expiring = response.data.events?.find(ev => ev.event == 'Expiring')?.isActive;

        this.preferencesform.setValue({
          modified: modified,
          reachFinalState: finalState,
          expiring: expiring,
          frequency: response.data.frequency
        });
      }
    });
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
